// ABOUTME: App-bar trigger of the user menu: avatar with initials, an optional caption over the name.
// ABOUTME: A real `<button>`, so keyboard and screen readers open the menu for free.

import { alpha, Box, Divider } from '@ds/coolshop';

type UserMenuButtonProps = {
    name: string;
    /** small line over the name, e.g. the active market */
    caption?: string;
    open: boolean;
    menuId: string;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
};

/** px: the separator is shorter than the 64px app bar and centred in it */
const DIVIDER_HEIGHT = 34;

/** px: larger than the DS Avatar's biggest size (36px), so the circle is drawn here */
const AVATAR_SIZE = 40;

/** px: tight line heights keep the two lines (12px, 14px) close and centred on the avatar */
const CAPTION_LINE_HEIGHT = 16;
const NAME_LINE_HEIGHT = 20;

const getInitials = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('');

export const UserMenuButton = ({ name, caption, open, menuId, onClick }: UserMenuButtonProps) => (
    // gap + the button's padding = the app bar's 16px gap, so the separator sits centred between
    // the previous icon and the avatar.
    <Box display="flex" alignItems="center" gap={1.5}>
        <Divider orientation="vertical" height={DIVIDER_HEIGHT} />
        <Box
            component="button"
            aria-haspopup="true"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
                // `Box` types its props on a div, so the element cannot declare `type="button"`.
                event.preventDefault();
                onClick(event);
            }}
            display="flex"
            alignItems="center"
            gap={1.5}
            padding={0.5}
            cursor="pointer"
            fontFamily="inherit"
            textAlign="left"
            border={0}
            borderRadius={1}
            bgcolor={open ? 'action.selected' : 'transparent'}
        >
            {/* The DS primary Avatar's colours, at the app bar's avatar size. */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                width={AVATAR_SIZE}
                height={AVATAR_SIZE}
                borderRadius="50%"
                fontSize={20}
                color="primary.main"
                bgcolor={t => alpha(t.palette.primary.main, t.palette.action.focusOpacity)}
            >
                {getInitials(name)}
            </Box>
            {/* Plain text boxes rather than Typography: the DS Typography takes no lineHeight, and
                the variants' own line heights spread the two lines apart. */}
            <Box display="flex" flexDirection="column" minWidth={0}>
                {caption && (
                    <Box
                        component="span"
                        fontSize={12}
                        fontWeight={400}
                        lineHeight={`${CAPTION_LINE_HEIGHT}px`}
                        color="text.secondary"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                    >
                        {caption}
                    </Box>
                )}
                <Box
                    component="span"
                    fontSize={14}
                    fontWeight={500}
                    lineHeight={`${NAME_LINE_HEIGHT}px`}
                    color="text.primary"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                >
                    {name}
                </Box>
            </Box>
        </Box>
    </Box>
);

export default UserMenuButton;
